import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import {
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  ArrowRight,
  FlaskConical,
  Wifi,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuthStore } from "@/stores/auth.store";
import { api } from "@/lib/api-client";
import { MOCK_CREDENTIALS, DEMO_TENANT_ID } from "@/lib/mock-auth";
import { useDemoMode, useDemoModeStore } from "@/lib/demo-mode";
import {
  currentTenantResolutionStrategy,
  resolveTenantId,
  resolveTenantIdBySubdomain,
  resolveTenantIdBySchoolCode,
} from "@/lib/tenant-resolution";
import { authApi, type TenantMatch } from "@/features/auth/api/auth.api";
import type { User, LoginResponse } from "@/types/auth";
import type { ApiResponse } from "@/types/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AxiosError } from "axios";

const isDev = import.meta.env.VITE_APP_ENV === "development";

// ── Full permission set for demo / dev fallback ───────────────────────────────
const DEMO_PERMISSIONS = [
  "admin:all",
  "tenants:manage", "users:manage", "roles:manage", "permissions:read",
  "students:read", "students:write", "students:delete",
  "staff:read", "staff:write", "staff:delete",
  "guardians:read", "guardians:write",
  "academic-years:manage", "academic-terms:manage",
  "departments:read", "departments:write",
  "grade-levels:read", "grade-levels:write",
  "streams:read", "streams:write",
  "subjects:read", "subjects:write",
  "curriculum:read", "curriculum:write",
  "rooms:read", "rooms:write",
  "enrollment:manage", "assignments:read", "assignments:write",
  "timetable:read", "timetable:write",
  "grades:manage", "assessments:read", "assessments:write",
  "report-cards:read", "report-cards:write", "report-cards:publish",
  "attendance:manage",
  "fees:manage", "invoices:manage", "payments:read", "payments:write",
  "notifications:manage", "announcements:manage", "messages:read", "messages:write",
  "reports:read", "reports:write", "reports:export",
  "exams:manage", "exam-results:read", "exam-results:write",
  "discipline:manage",
  "admissions:manage",
  "events:read", "events:write",
  "health:read", "health:write",
  "documents:read", "documents:write",
  "config:read", "config:write",
  "audit-logs:read",
  "mfa:manage", "jobs:manage",
  "facilities:manage",
  "hostel:manage",
  "transport:read", "transport:write", "transport:manage",
  "transport:dispatch", "transport:track", "transport:delete",
  "payroll:manage", "leave:manage",
  "library:read", "library:write", "library:manage",
  "inventory:read", "inventory:write", "inventory:manage",
];

type LoginFormValues = { email: string; password: string; schoolCode?: string };

/* ─── Shared hook: login auth logic ──────────────────────── */

export function useLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // email-first: when an email resolves to more than one tenant, the user
  // must pick which org to sign into before password entry proceeds.
  const [tenantChoices, setTenantChoices] = useState<TenantMatch[] | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  // field strategy: error shown when the user-supplied school code doesn't resolve.
  const [schoolCodeError, setSchoolCodeError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setMfaPending = useAuthStore((s) => s.setMfaPending);
  const { t } = useTranslation("common");
  const { demoMode } = useDemoMode();
  const strategy = currentTenantResolutionStrategy();

  const loginSchema = z.object({
    email: z.email(t("auth.invalidEmail")),
    password: z.string().min(1, t("auth.passwordRequired")),
    ...(strategy === "field"
      ? { schoolCode: z.string().min(1, t("auth.schoolCodeRequired", "School code is required")) }
      : {}),
  });

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ?? "/";

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function handleLoginSuccess(data: LoginResponse) {
    if (data.mfa_required && data.mfa_challenge_token) {
      setMfaPending(data.mfa_challenge_token, data.mfa_method ?? null);
      navigate("/mfa-verify");
      return;
    }

    // Tokens are in HttpOnly cookies — do NOT read from response body.
    // The browser sends the access_token cookie automatically (withCredentials).
    let permissions: string[] = [];
    try {
      const meResponse = await api.get<
        ApiResponse<{ permissions: string[]; roles: string[] } & User>
      >("/auth/me");
      permissions = meResponse.data.data.permissions ?? [];
    } catch {
      // DEV-ONLY fallback: if /auth/me is unreachable during development, grant a
      // full admin set so all nav items stay visible. In production this branch
      // MUST leave permissions empty — otherwise a transient /auth/me failure
      // would unlock the entire UI. The backend remains the sole authority.
      permissions = import.meta.env.DEV
        ? [
        "admin:all",
        "tenants:manage", "users:manage", "roles:manage", "permissions:read",
        "students:read", "students:write", "students:delete",
        "staff:read", "staff:write", "staff:delete",
        "guardians:read", "guardians:write",
        "academic-years:manage", "academic-terms:manage",
        "departments:read", "departments:write",
        "grade-levels:read", "grade-levels:write",
        "streams:read", "streams:write",
        "subjects:read", "subjects:write",
        "curriculum:read", "curriculum:write",
        "rooms:read", "rooms:write",
        "enrollment:manage", "assignments:read", "assignments:write",
        "timetable:read", "timetable:write",
        "grades:manage", "assessments:read", "assessments:write",
        "report-cards:read", "report-cards:write", "report-cards:publish",
        "attendance:manage",
        "fees:manage", "invoices:manage", "payments:read", "payments:write",
        "notifications:manage", "announcements:manage", "messages:read", "messages:write",
        "reports:read", "reports:write", "reports:export",
        "exams:manage", "exam-results:read", "exam-results:write",
        "discipline:manage",
        "admissions:manage",
        "events:read", "events:write",
        "health:read", "health:write",
        "documents:read", "documents:write",
        "config:read", "config:write",
        "audit-logs:read",
        "mfa:manage", "jobs:manage",
        "facilities:manage",
        "hostel:manage",
        "transport:read", "transport:write", "transport:manage",
        "transport:dispatch", "transport:track", "transport:delete",
        "payroll:manage", "leave:manage",
        "library:read", "library:write", "library:manage",
        "inventory:read", "inventory:write", "inventory:manage",
          ]
        : [];
    }

    setAuth({
      user: data.user,
      permissions,
      // Tokens live in HttpOnly cookies; no need to store them here.
    });
    const displayName = data.user.display_name || data.user.full_name;
    toast.success(t("auth.welcomeBack", { name: displayName }));
    navigate(from, { replace: true });
  }

  // Resolves which tenant_id to submit with the login request, per the
  // configured VITE_TENANT_RESOLUTION strategy. Returns null in two cases:
  //   - "email-first" + multiple orgs matched: tenantChoices is populated,
  //     the form renders a picker, and the user must choose before proceeding.
  //   - "field" + school code not found: schoolCodeError is set with an inline
  //     error message; the caller should abort the submit.
  // Every other outcome returns a usable tenant_id, falling back to the
  // hardcoded DEMO_TENANT_ID if resolution fails so login is never blocked.
  async function resolveTenantForSubmit(
    email: string,
    schoolCode?: string,
  ): Promise<string | null> {
    if (selectedTenantId) return selectedTenantId;

    if (strategy === "hardcoded") return DEMO_TENANT_ID;

    if (strategy === "subdomain") {
      return resolveTenantIdBySubdomain();
    }

    if (strategy === "field") {
      // The school code field is required in the schema, so it will always be
      // set here — but guard defensively.
      if (!schoolCode?.trim()) {
        setSchoolCodeError(t("auth.schoolCodeRequired", "School code is required"));
        return null;
      }
      setSchoolCodeError(null);
      try {
        const match = await resolveTenantIdBySchoolCode(schoolCode);
        return match.tenant_id;
      } catch {
        setSchoolCodeError(
          t("auth.schoolNotFound", "School not found. Check your school code."),
        );
        return null;
      }
    }

    if (strategy === "email-first") {
      let matches: TenantMatch[] = [];
      try {
        matches = await authApi.resolveTenant(email);
      } catch {
        // Resolution endpoint unreachable — fall back rather than block login.
        return DEMO_TENANT_ID;
      }
      if (matches.length === 0) return DEMO_TENANT_ID;
      if (matches.length === 1) return matches[0].tenant_id;
      // Multiple orgs share this email — render the picker.
      setTenantChoices(matches);
      return null;
    }

    return resolveTenantId(strategy, null);
  }

  function chooseTenant(tenantId: string) {
    setSelectedTenantId(tenantId);
    setTenantChoices(null);
  }

  async function onSubmit(values: LoginFormValues) {
    setIsLoading(true);
    try {
      // ── Demo mode: bypass real API, validate against mock credentials ──────
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 600)); // simulate network
        const match = MOCK_CREDENTIALS.find(
          (c) => c.email === values.email && c.password === values.password,
        );
        if (!match) {
          toast.error(t("auth.invalidCredentials"));
          return;
        }
        const mockUser: User = {
          id: "demo-user-001",
          tenant_id: DEMO_TENANT_ID,
          email: match.email,
          full_name: match.label,
          display_name: match.label,
          preferred_language: "en",
          status: "active",
          is_active: true,
          version: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setAuth({
          user: mockUser,
          accessToken: "demo-access-token",
          refreshToken: "demo-refresh-token",
          permissions: DEMO_PERMISSIONS,
        });
        toast.success(t("auth.welcomeBack", { name: match.label }));
        navigate(from, { replace: true });
        return;
      }

      // ── Production mode: real API ─────────────────────────────────────────
      const tenantId = await resolveTenantForSubmit(values.email, values.schoolCode);
      if (tenantId === null) {
        // Either multiple orgs matched this email (tenantChoices populated, picker
        // will render) or the field strategy's school code was invalid
        // (schoolCodeError set, inline error shown).
        return;
      }

      const response = await api.post<ApiResponse<LoginResponse>>(
        "/auth/login",
        {
          tenant_id: tenantId,
          login: values.email,
          password: values.password,
        },
      );
      await handleLoginSuccess(response.data.data);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data?.error?.detail) {
        toast.error(err.response.data.error.detail);
      } else {
        toast.error(t("auth.invalidCredentials"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  function fillCredentials(email: string) {
    const cred = MOCK_CREDENTIALS.find((c) => c.email === email);
    form.setValue("email", email);
    form.setValue("password", cred?.password ?? "");
  }

  return {
    form,
    showPassword,
    setShowPassword,
    isLoading,
    onSubmit,
    fillCredentials,
    t,
    demoMode,
    strategy,
    currentEmail: form.watch("email"),
    tenantChoices,
    chooseTenant,
    cancelTenantChoice: () => setTenantChoices(null),
    schoolCodeError,
  };
}

/* ─── Reusable login form component ──────────────────────── */

interface LoginFormProps {
  variant?: "default" | "dark" | "inline";
}

export function LoginForm({ variant = "default" }: LoginFormProps) {
  const {
    form,
    showPassword,
    setShowPassword,
    isLoading,
    onSubmit,
    fillCredentials,
    t,
    demoMode,
    strategy,
    currentEmail,
    tenantChoices,
    chooseTenant,
    cancelTenantChoice,
    schoolCodeError,
  } = useLoginForm();

  const isDark = variant === "dark";
  const isInline = variant === "inline";

  // Email-first resolution found more than one org for this email — the user
  // must pick which one to sign into before password entry proceeds.
  if (tenantChoices && tenantChoices.length > 1) {
    return (
      <div className={cn("w-full space-y-5", isInline && "space-y-4")}>
        <div className="space-y-1">
          <p className={cn("text-sm font-medium", isDark && "text-white/80")}>
            {t("auth.chooseOrganization", "Which organization do you want to sign into?")}
          </p>
          <p className={cn("text-xs text-muted-foreground", isDark && "text-white/50")}>
            {currentEmail}
          </p>
        </div>
        <div className="space-y-2">
          {tenantChoices.map((tenant) => (
            <button
              key={tenant.tenant_id}
              type="button"
              onClick={() => chooseTenant(tenant.tenant_id)}
              className={cn(
                "flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors",
                isDark
                  ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                  : "border-border bg-muted/30 hover:bg-muted/60",
              )}
            >
              {tenant.tenant_name}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={cancelTenantChoice}
          className={cn(
            "text-xs font-medium hover:underline",
            isDark ? "text-white/60" : "text-muted-foreground",
          )}
        >
          {t("common.back", "Back")}
        </button>
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-6", isInline && "space-y-5")}>
      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* School code field — only rendered for the "field" strategy */}
          {strategy === "field" && (
            <FormField
              control={form.control}
              name="schoolCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={cn(
                      "text-sm font-medium",
                      isDark && "text-white/80",
                    )}
                  >
                    {t("auth.schoolCode", "School Code")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={t("auth.schoolCodePlaceholder", "e.g. makini-school")}
                      autoComplete="organization"
                      className={cn(
                        "h-11 rounded-lg transition-colors",
                        isDark
                          ? "border-white/10 bg-white/10 text-white placeholder:text-white/40 focus:bg-white/15"
                          : "bg-muted/40 focus:bg-background",
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  {/* Server-side validation error from slug lookup */}
                  {schoolCodeError && (
                    <p className="text-[0.8rem] font-medium text-destructive">
                      {schoolCodeError}
                    </p>
                  )}
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className={cn(
                    "text-sm font-medium",
                    isDark && "text-white/80",
                  )}
                >
                  {t("auth.email")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("auth.emailPlaceholder")}
                    autoComplete="email"
                    autoFocus
                    className={cn(
                      "h-11 rounded-lg transition-colors",
                      isDark
                        ? "border-white/10 bg-white/10 text-white placeholder:text-white/40 focus:bg-white/15"
                        : "bg-muted/40 focus:bg-background",
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel
                    className={cn(
                      "text-sm font-medium",
                      isDark && "text-white/80",
                    )}
                  >
                    {t("auth.password")}
                  </FormLabel>
                  <Link
                    to="/forgot-password"
                    className={cn(
                      "text-xs font-medium transition-colors hover:underline",
                      isDark
                        ? "text-white/60 hover:text-white/80"
                        : "text-primary hover:text-primary/80",
                    )}
                  >
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth.passwordPlaceholder")}
                      autoComplete="current-password"
                      className={cn(
                        "h-11 rounded-lg pr-11 transition-colors",
                        isDark
                          ? "border-white/10 bg-white/10 text-white placeholder:text-white/40 focus:bg-white/15"
                          : "bg-muted/40 focus:bg-background",
                      )}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={cn(
                        "absolute right-0 top-0 flex h-11 w-11 items-center justify-center transition-colors",
                        isDark
                          ? "text-white/40 hover:text-white/70"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      aria-label={showPassword ? t("auth.hidePassword", "Hide password") : t("auth.showPassword", "Show password")}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remember me */}
          <div className="flex items-center gap-2">
            <Checkbox id="remember" />
            <label
              htmlFor="remember"
              className={cn(
                "text-sm select-none cursor-pointer",
                isDark ? "text-white/60" : "text-muted-foreground",
              )}
            >
              {t("auth.rememberMe")}
            </label>
          </div>

          {/* Demo mode toggle */}
          <button
            type="button"
            onClick={() => useDemoModeStore.getState().toggleDemoMode()}
            className={cn(
              "flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-sm transition-colors",
              demoMode
                ? isDark
                  ? "border-amber-400/30 bg-amber-400/10 text-amber-300"
                  : "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300"
                : isDark
                  ? "border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70"
                  : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <span className="flex items-center gap-2 font-medium">
              {demoMode ? (
                <FlaskConical className="h-4 w-4" />
              ) : (
                <Wifi className="h-4 w-4" />
              )}
              {demoMode ? "Demo mode" : "Live mode"}
            </span>
            <span className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
              demoMode
                ? "bg-amber-200/60 text-amber-800 dark:bg-amber-400/20 dark:text-amber-300"
                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300",
            )}>
              {demoMode ? "mock data" : "real API"}
            </span>
          </button>

          {/* Submit */}
          <Button
            type="submit"
            className={cn(
              "group h-11 w-full rounded-lg font-semibold text-[15px] transition-all",
              isDark &&
                "bg-white text-zinc-950 hover:bg-white/90",
            )}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="mr-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            )}
            {t("auth.signIn")}
          </Button>
        </form>
      </Form>

      {/* Security badge */}
      <div
        className={cn(
          "flex items-center justify-center gap-1.5 text-xs",
          isDark ? "text-white/30" : "text-muted-foreground/60",
        )}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        <span>{t("auth.sslEncrypted")}</span>
      </div>

      {/* Demo accounts (visible in dev mode or when demo mode is active) */}
      {(isDev || demoMode) && (
        <>
          <div className="relative">
            <Separator className={isDark ? "bg-white/10" : undefined} />
            <span
              className={cn(
                "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs font-medium",
                isDark
                  ? "bg-zinc-950 text-white/50"
                  : "bg-background text-muted-foreground",
              )}
            >
              {t("auth.demoAccounts")}
            </span>
          </div>

          <div className="space-y-2">
            <Select
              value={currentEmail || undefined}
              onValueChange={(email) => fillCredentials(email)}
            >
              <SelectTrigger
                className={cn(
                  "h-10 w-full rounded-lg",
                  isDark
                    ? "border-white/10 bg-white/10 text-white [&>svg]:text-white/40"
                    : "bg-muted/40",
                )}
              >
                <SelectValue
                  placeholder={t("auth.demoAccounts")}
                />
              </SelectTrigger>
              <SelectContent>
                {MOCK_CREDENTIALS.map((cred) => (
                  <SelectItem key={cred.email} value={cred.email}>
                    <span className="flex items-center gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                        {cred.label[0]}
                      </span>
                      <span className="font-medium">{cred.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {cred.email}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p
              className={cn(
                "text-center text-[11px]",
                isDark ? "text-white/25" : "text-muted-foreground/50",
              )}
            >
              {t("auth.demoPasswordHint")}{" "}
              <code className="font-mono">password</code>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Default export: standalone login page ──────────────── */

export function LoginPage() {
  const { t } = useTranslation("common");

  return (
    <div className="w-full max-w-[400px] space-y-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t("auth.signIn")}
        </h1>
        <p className="text-sm text-muted-foreground text-balance leading-relaxed">
          {t("auth.signInDescription")}
        </p>
      </div>
      <LoginForm />
    </div>
  );
}
